import React from 'react';
import _isEmpty from 'lodash/isEmpty';
import { v4 as uuid } from 'uuid';

import {
  logActivity as logActivityService,
  logAutoCompleteActivity,
  logCompanyReviewSubmittedActivity,
  logDLPJobActivity,
  logJobUploadResumeActivity,
  logProfileUpdateActivity,
} from 'services/logActivity';
import { TRACKING, USER_ACTIVITY_EVENTS } from 'utils/constant';

const generateUniqueId = () => `${uuid().substring(0, 4)}${Date.now()}`;

export const useTracking = (options = {}) => {
  const { jobs = [], apiParams = '', logExtraFields = true } = options;

  const allowedJobActivityTypes = [
    USER_ACTIVITY_EVENTS.JOB_OPENING.JOB_OPENING_VIEWED_DLP,
    USER_ACTIVITY_EVENTS.JOB_OPENING.JOB_OPENING_JOB_CLICKED,
    USER_ACTIVITY_EVENTS.JOB_OPENING.JOB_OPENING_JOB_CLICKED_DLP,
  ];

  const getCommonFields = (job = {}) =>
    !_isEmpty(job)
      ? {
          cpc: job.cpc,
          city: job.OBJcity,
          jobTitle: job.OBJtitle,
          companyID: job.companyID,
          stateCode: job.OBJstateCode,
          listingHash: job.listingHash,
          companyName: job.companyName,
          postingDate: job.postingDate,
          jobTitleDisplay: job.jobTitle,
          unifiedZippiaSalary: job.unifiedZippiaSalary,
        }
      : {};

  const getJobFields = (job = {}, allJobs, optionalApiParams) => {
    const allUIJobs = allJobs || jobs;
    const allUIApiParams = optionalApiParams || apiParams;
    const index = allUIJobs.findIndex((j) => j.listingHash === job.listingHash);

    if (index !== -1) {
      return {
        nJob: index + 1,
        numJobs: allUIJobs.length,
        postingDate: job.postingDate,
        ...(!_isEmpty(allUIApiParams) ? { apiParams: allUIApiParams } : {}),
        previousJobListings: allUIJobs.slice(0, index).map((job, idx) => ({
          nJob: idx + 1,
          ...getCommonFields(job),
        })),
      };
    } else if (!_isEmpty(job)) {
      return {
        nJob: 1,
        numJobs: 1,
        postingDate: job.postingDate,
        ...(!_isEmpty(allUIApiParams) ? { apiParams: allUIApiParams } : {}),
        previousJobListings: [],
      };
    } else {
      return {};
    }
  };

  /**
   * Logging activity
   *
   * @param {Object} payload - logging args
   * @param {String} activityType - type of activity
   */
  const logActivity = (data, activityType, allJobs = undefined, apiParams = undefined) => {
    const {
      job,
      type,
      payload = {},
      pageCategory,
      companyId,
      source,
      jobData,
      resumeLink,
      fieldUpdated,
      fieldChangedFrom,
      fieldChangedTo,
    } = data;

    switch (activityType) {
      case TRACKING.LOG_ACTIVITY.AUTO_COMPLETE:
      case TRACKING.LOG_ACTIVITY.HEADER_SEARCH:
        logAutoCompleteActivity(data);
        break;
      case TRACKING.LOG_ACTIVITY.COMPANY_REVIEW_SUBMITTED:
        logCompanyReviewSubmittedActivity(companyId, source);
        break;
      case TRACKING.LOG_ACTIVITY.DLP:
        const index = allowedJobActivityTypes.findIndex((t) => t === type);
        const newPayload =
          index !== -1 && !!logExtraFields
            ? { ...payload, ...getJobFields(job, allJobs, apiParams) }
            : payload;
        logDLPJobActivity(job, type, newPayload, pageCategory);
        break;
      case TRACKING.LOG_ACTIVITY.JOB_UPLOAD_RESUME:
        logJobUploadResumeActivity(jobData, resumeLink, job);
        break;
      case TRACKING.LOG_ACTIVITY.PROFILE_UPDATE:
        logProfileUpdateActivity(fieldUpdated, fieldChangedFrom, fieldChangedTo);
        break;
      default:
        logActivityService(data);
    }
  };

  /**
   * Open job link in new tab
   *
   * @param {Object} data - external link
   * @param {String} data.source - source of job
   * @param {String} data.OBJurl - external job url
   * @param {String} data.listingHash - job listing hash
   *
   * @param {Object} options - activity options
   * @param {String} options.activityType - type of activity
   * @param {Object} options.activityData - args of activity
   */
  const openJobExternal = (data = {}, options = {}) => {
    /* * * * * * * * * * * * * * * * * * * * NOTE * * * * * * *  * * *  * * *  * * *  * * *  * */
    /*   if you update this method then please update the code at Server side, there we have   */
    /*   same logic at method "withClickID" at helpers/utils.js                                */
    /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

    const { OBJurl } = data;
    const { activityType = {}, activityData, returnExternalUrl = false } = options;

    const logData = {
      ...activityData,
      job: { ...data },
    };

    let externalUrl = OBJurl;

    const source = TRACKING.SOURCES.get(data.source);

    if (source) {
      const { param } = source;

      const url = new URL(OBJurl);
      const params = new URLSearchParams(url?.search);

      const id = generateUniqueId();

      params.has(param) ? params.set(param, id) : params.append(param, id);

      /* sometimes url might have key only not value like https://www.jobg8.com/ATSApply.aspx?It2OQbh%2Bx8BZxWN8apv6DQi  so it means search query is ?It2OQbh%2Bx8BZxWN8apv6DQi here we have key only which is 'It2OQbh%2Bx8BZxWN8apv6DQi'
            and automatically value considered '' (empty string) so url be like https://www.jobg8.com/ATSApply.aspx?It2OQbh%2Bx8BZxWN8apv6DQi='' so if you try to do search.toString() it will add '=' sign at the end automatically considering empty string value.
            so in this case search value will be ?It2OQbh%2Bx8BZxWN8apv6DQi= that's why we must have to manually join the search params. (bug/FRON-11768) */
      const query = [...params.entries()]
        .map((keyValue) =>
          keyValue
            .filter(Boolean)
            .map((x) => encodeURIComponent(x))
            .join('=')
        )
        .join('&');

      url.search = query;

      externalUrl = decodeURIComponent(url.toString());
    }

    if (externalUrl) {
      logData.job.OBJurl = externalUrl;
    }

    if (activityType && logData) {
      logActivity(logData, activityType, options.allJobs, options.apiParams);
    }

    if (returnExternalUrl) {
      return externalUrl;
    }

    window.open(externalUrl, '_blank');
  };

  return {
    logActivity,
    openJobExternal,
  };
};

export const withTracking = (Component, options = {}) => {
  const RenderedComponent = (props) => {
    return (
      <Component
        {...props}
        tracking={useTracking({
          jobs: props.allJobs || props.jobs || [],
          logExtraFields: options.logExtraFields,
          apiParams: props.apiParams || '',
        })}
      />
    );
  };

  RenderedComponent.getInitialProps = Component.getInitialProps;

  return RenderedComponent;
};
